import { useLingui } from '@lingui/react/macro';

import ArrowDownIcon from '@watershed/icons/components/ArrowDown';
import ArrowUpIcon from '@watershed/icons/components/ArrowUp';
import mixinSx from '@watershed/style/mixinSx';

import {
  GridColDef,
  GridSortItem,
  GridSortModel,
  useGridApiContext,
} from '../DataGrid/DataGrid';
import DropdownMenu, { DropdownMenuProps } from '../DropdownMenu';

/**
 * This component takes in a sortModel and setSortModel function
 * as props for cases where it is being used with server side sorting.
 *
 * (This should almost always be the case!)
 *
 * Otherwise it falls back to grabbing the sort model from the API ref (:womp:),
 * and you can starting drafting the post-mortem now!
 */
export function ObjectListToolbarQuickSort({
  sortModel: sortModelProp,
  setSortModel: setSortModelProp,
  dropdownMenuProps,
  excludeColumns = [],
  testId,
  defaultSortDirections,
}: {
  sortModel?: GridSortModel;
  setSortModel?: (sortModel: GridSortModel) => void;
  dropdownMenuProps?: Partial<DropdownMenuProps>;
  excludeColumns?: Array<string>;
  testId?: string;
  /** Map of field names to their default sort direction */
  defaultSortDirections?: Record<string, 'asc' | 'desc'>;
}) {
  const { t } = useLingui();
  const apiRef = useGridApiContext();
  const { getSortModel, getAllColumns } = apiRef.current;

  // Exclude the checkbox selection column by default
  const excludeColumnsWithCheck = [...excludeColumns, '__check__'];
  const columns = getAllColumns().filter(
    (col) =>
      col.headerName &&
      col.headerName.length > 0 &&
      !excludeColumnsWithCheck.includes(col.field)
  );
  if (columns.length === 0) return null;

  const sortModel = sortModelProp ?? getSortModel();
  const setSortModel = setSortModelProp ?? apiRef.current.setSortModel;
  const currentSort = sortModel.length > 0 ? sortModel[0] : null;

  const currentSortName = getCurrentSortName({ currentSort, columns });
  const currentSortDir = currentSort?.sort ?? 'asc';

  return (
    <DropdownMenu
      testId={testId}
      trigger={
        currentSortName
          ? t({
              message: `Sort by: ${currentSortName}`,
              context: 'Button copy for dropdown to change sort for a list',
            })
          : t({
              message: 'Sort',
              context: 'Button copy for dropdown to change sort for a list',
            })
      }
      triggerStartIcon={
        currentSort ? (
          <ArrowDownIcon
            color="text.secondary"
            style={
              currentSort.sort === 'asc'
                ? { rotate: '180deg' }
                : { rotate: '0deg' }
            }
            sx={{ transition: (theme) => theme.transitions.create('rotate') }}
          />
        ) : null
      }
      buttonProps={{
        'aria-label': currentSort
          ? !currentSort || currentSort.sort
            ? t({
                message: `Sort by ${currentSortName} ascending`,
                context: 'Button copy for dropdown to change sort for a list',
              })
            : t({
                message: `Sort by ${currentSortName} descending`,
                context: 'Button copy for dropdown to change sort for a list',
              })
          : t({
              message: 'Sort',
              context: 'Button copy for dropdown to change sort for a list',
            }),
      }}
      items={columns.map((col) => ({
        id: col.field,
        label: col.headerName,
        Icon:
          col.field === currentSort?.field
            ? currentSortDir === 'asc'
              ? ArrowUpIcon
              : ArrowDownIcon
            : undefined,
        innardSx:
          col.field === currentSortName
            ? { flexDirection: 'row-reverse', width: '100%' }
            : undefined,
        onSelect: () =>
          // If we are not changing the sort field, we just toggle the sort direction
          currentSort?.field === col.field
            ? setSortModel([
                {
                  field: col.field,
                  sort: currentSortDir === 'asc' ? 'desc' : 'asc',
                },
              ])
            : // If we are changing the sort field, we set the new sort field's sort direction to its default
              setSortModel([
                {
                  field: col.field,
                  sort: defaultSortDirections?.[col.field] ?? 'asc',
                },
              ]),
      }))}
      {...dropdownMenuProps}
      menuSx={mixinSx(
        {
          '& .MuiMenu-paper, & .MuiMenu-list': {
            minWidth: 200,
          },
        },
        dropdownMenuProps?.menuSx
      )}
    />
  );
}

function getCurrentSortName({
  currentSort,
  columns,
}: {
  currentSort: GridSortItem | null;
  columns: Array<GridColDef>;
}): string | null {
  if (!currentSort) return null;

  const col = columns.find((col) => col.field === currentSort.field);
  if (!col) return null;

  const { headerName } = col;
  if (!headerName) return null;

  // Remove any parentheticals from the header name
  return headerName.split('(')[0];
}
